// src/features/crypto-ranking/components/CryptoDetailPopup.tsx
//
// Tabbed detail modal for a single crypto coin. Opened by clicking a ticker
// in the universe table. Mirrors the polished UX of <FXDetailPopup/> but
// without COT (crypto has no positioning feed). Three tabs:
//   - PRICE   : daily USD close (area) + USD turnover (bars) from
//               GET /api/v1/crypto-ranking/price-history/{symbol}.
//   - SIGNAL  : composite Forge Ultra Signal line from
//               GET /api/v1/crypto-ranking/signal/{symbol}.
//   - FACTORS : momentum / volatility / liquidity component z-scores (lines)
//               + annualized volatility (bars) from
//               GET /api/v1/crypto-ranking/factors/{symbol}.
//
// Header stats (rank + composite signal) come from a one-shot
// GET /overview/{symbol}. All four requests are fired in parallel on open
// and cancelled on close / unmount. Esc closes; backdrop click closes; the
// dialog traps focus visually via the modal overlay.

import { type ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Area,
    Bar,
    CartesianGrid,
    ComposedChart,
    Legend,
    Line,
    LineChart,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import {
    baseAsset,
    fetchFactors,
    fetchOverview,
    fetchPriceHistory,
    fetchSignal,
    type FactorsResponse,
    type OverviewResponse,
    type PriceHistoryResponse,
    type SignalResponse,
} from '../api';

type TabKey = 'price' | 'signal' | 'factors';

const TABS: { key: TabKey; label: string }[] = [
    { key: 'price', label: 'Price / Volume' },
    { key: 'signal', label: 'Signal' },
    { key: 'factors', label: 'Factors' },
];

const HISTORY_DAYS = 365;

// Crypto prices span several orders of magnitude (SHIB ~0.00002, BTC
// ~61000), so a fixed-decimal formatter is unsuitable -- significant figures.
const formatPrice = (value: number | null): string => {
    if (value == null || !Number.isFinite(value)) return 'N/A';
    return value.toPrecision(5);
};

const formatVolume = (value: number | null): string => {
    if (value == null || !Number.isFinite(value)) return 'N/A';
    const abs = Math.abs(value);
    if (abs >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
    if (abs >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
    if (abs >= 1e3) return `$${(value / 1e3).toFixed(1)}k`;
    return `$${value.toFixed(0)}`;
};

const formatZ = (value: number | null): string =>
    value == null || !Number.isFinite(value) ? 'N/A' : value.toFixed(2);

interface ChartStateProps {
    isLoading: boolean;
    error: string | null;
    isEmpty: boolean;
    emptyMessage: string;
    children: ReactNode;
}

// Shared loading / error / empty gate so each tab body stays declarative.
const ChartState = ({
    isLoading,
    error,
    isEmpty,
    emptyMessage,
    children,
}: ChartStateProps) => {
    if (isLoading) {
        return (
            <div className="space-y-3" aria-busy="true">
                <div className="h-72 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-800" />
            </div>
        );
    }
    if (error) {
        return (
            <div className="rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-700 dark:text-red-400">
                {error}
            </div>
        );
    }
    if (isEmpty) {
        return (
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 px-3 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                {emptyMessage}
            </div>
        );
    }
    return <>{children}</>;
};

interface HeaderStatProps {
    label: string;
    value: string;
}

const HeaderStat = ({ label, value }: HeaderStatProps) => (
    <div className="rounded-lg bg-slate-50 dark:bg-slate-800/60 px-3 py-2">
        <p className="text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500">
            {label}
        </p>
        <p className="text-sm font-semibold text-slate-900 dark:text-white">
            {value}
        </p>
    </div>
);

interface CryptoDetailPopupProps {
    symbol: string;
    onClose: () => void;
}

export const CryptoDetailPopup = ({
    symbol,
    onClose,
}: CryptoDetailPopupProps) => {
    const navigate = useNavigate();
    const [tab, setTab] = useState<TabKey>('price');
    const [price, setPrice] = useState<PriceHistoryResponse | null>(null);
    const [factors, setFactors] = useState<FactorsResponse | null>(null);
    const [signal, setSignal] = useState<SignalResponse | null>(null);
    const [overview, setOverview] = useState<OverviewResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Navigate to the full crypto-info deep-dive page. The ranking universe
    // keys coins by their FMP pair (e.g. "BTCUSD"), but the crypto-info page
    // and its static description JSON use the base ticker (e.g. "BTC").
    const handleMoreInfo = () => {
        onClose();
        navigate(
            `/dashboard/crypto-info-page?symbol=${encodeURIComponent(baseAsset(symbol))}`,
        );
    };

    // Fetch price + signal + factor history + overview once per symbol, in
    // parallel. Overview is best-effort (404 for a coin not in today's
    // screened universe) so its failure must not blank the charts.
    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        setError(null);
        setPrice(null);
        setFactors(null);
        setSignal(null);
        setOverview(null);
        (async () => {
            try {
                const [p, f, s, o] = await Promise.all([
                    fetchPriceHistory(symbol, HISTORY_DAYS),
                    fetchFactors(symbol, HISTORY_DAYS),
                    fetchSignal(symbol, HISTORY_DAYS),
                    fetchOverview(symbol).catch(() => null),
                ]);
                if (cancelled) return;
                setPrice(p);
                setFactors(f);
                setSignal(s);
                setOverview(o);
            } catch (err) {
                if (cancelled) return;
                // eslint-disable-next-line no-console
                console.error('Failed to load crypto detail', err);
                setError('Unable to load detail data for this coin.');
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [symbol]);

    // Escape-to-close + body scroll lock.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [onClose]);

    const priceSeries = price?.series ?? [];
    const factorSeries = factors?.series ?? [];
    const signalSeries = signal?.series ?? [];
    const displayName = baseAsset(symbol);
    const hasVolume = priceSeries.some(
        (b) => b.volume != null && b.volume > 0,
    );

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
            onClick={onClose}
            role="presentation"
        >
            <div
                className="relative w-full max-w-5xl rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label={`${symbol} detail`}
            >
                <button
                    type="button"
                    className="absolute top-4 right-4 flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200 transition-colors"
                    onClick={onClose}
                    aria-label="Close"
                >
                    ✕
                </button>

                <h2 className="text-2xl font-bold text-slate-900 dark:text-white pr-8">
                    {displayName}
                </h2>
                <p className="mt-0.5 text-sm font-medium text-orange-600 dark:text-orange-400">
                    {symbol}
                    <span className="ml-2 rounded-md bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-xs font-medium text-slate-500 dark:text-slate-400">
                        USD pair
                    </span>
                </p>

                {/* Deep-dive entry point: opens the full crypto-info page with
                    the live quote, intraday chart, and quantitative breakout
                    alert for this coin. */}
                <button
                    type="button"
                    onClick={handleMoreInfo}
                    className="mt-3 inline-flex items-center gap-1 rounded-lg bg-orange-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900"
                >
                    More Info… <span aria-hidden="true">&rarr;</span>
                </button>

                {/* Header snapshot stats (latest cross-section). */}
                {overview && (
                    <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
                        <HeaderStat
                            label="Universe Rank"
                            value={`#${overview.universe_rank}`}
                        />
                        <HeaderStat
                            label="Forge Signal"
                            value={overview.forge_ultra_signal.toFixed(2)}
                        />
                        <HeaderStat
                            label="Momentum (z)"
                            value={formatZ(overview.momentum_z_score)}
                        />
                        <HeaderStat
                            label="Vol (Ann.)"
                            value={`${(overview.annualized_volatility * 100).toFixed(1)}%`}
                        />
                    </div>
                )}

                {/* Tab switcher. */}
                <div
                    className="mt-4 mb-5 flex flex-wrap gap-2"
                    role="tablist"
                    aria-label="Crypto detail views"
                >
                    {TABS.map((t) => {
                        const active = tab === t.key;
                        return (
                            <button
                                key={t.key}
                                type="button"
                                role="tab"
                                aria-selected={active}
                                onClick={() => setTab(t.key)}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                    active
                                        ? 'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900'
                                        : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
                                }`}
                            >
                                {t.label}
                            </button>
                        );
                    })}
                </div>

                {/* PRICE / VOLUME */}
                {tab === 'price' && (
                    <ChartState
                        isLoading={isLoading}
                        error={error}
                        isEmpty={priceSeries.length === 0}
                        emptyMessage="No price history is available for this coin."
                    >
                        <div className="w-full h-80 text-slate-500 dark:text-slate-400">
                            <ResponsiveContainer width="100%" height="100%">
                                <ComposedChart
                                    data={priceSeries}
                                    margin={{
                                        top: 8,
                                        right: 8,
                                        bottom: 8,
                                        left: 8,
                                    }}
                                >
                                    <CartesianGrid
                                        strokeDasharray="3 3"
                                        className="stroke-slate-200 dark:stroke-slate-700"
                                    />
                                    <XAxis
                                        dataKey="date"
                                        tick={{
                                            fontSize: 10,
                                            fill: 'currentColor',
                                        }}
                                        tickLine={false}
                                        minTickGap={40}
                                    />
                                    <YAxis
                                        yAxisId="price"
                                        tick={{
                                            fontSize: 10,
                                            fill: 'currentColor',
                                        }}
                                        tickLine={false}
                                        width={56}
                                        domain={['auto', 'auto']}
                                        tickFormatter={(v) =>
                                            formatPrice(v as number)
                                        }
                                    />
                                    {hasVolume && (
                                        <YAxis
                                            yAxisId="volume"
                                            orientation="right"
                                            tick={{
                                                fontSize: 10,
                                                fill: 'currentColor',
                                            }}
                                            tickLine={false}
                                            width={56}
                                            tickFormatter={(v) =>
                                                formatVolume(v as number)
                                            }
                                        />
                                    )}
                                    <Tooltip
                                        formatter={(
                                            value: number | string,
                                            name: string,
                                        ) =>
                                            name === 'Volume (USD)'
                                                ? formatVolume(value as number)
                                                : formatPrice(value as number)
                                        }
                                        contentStyle={{
                                            fontSize: 12,
                                            borderRadius: 8,
                                        }}
                                    />
                                    <Legend wrapperStyle={{ fontSize: 12 }} />
                                    {hasVolume && (
                                        <Bar
                                            yAxisId="volume"
                                            dataKey="volume"
                                            name="Volume (USD)"
                                            barSize={6}
                                            fill="#f59e0b"
                                            opacity={0.45}
                                        />
                                    )}
                                    <Area
                                        yAxisId="price"
                                        type="monotone"
                                        dataKey="close"
                                        name="Close (USD)"
                                        stroke="#f97316"
                                        fill="#fdba74"
                                        fillOpacity={0.3}
                                        strokeWidth={2}
                                        dot={false}
                                    />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>
                    </ChartState>
                )}

                {/* SIGNAL */}
                {tab === 'signal' && (
                    <ChartState
                        isLoading={isLoading}
                        error={error}
                        isEmpty={signalSeries.length === 0}
                        emptyMessage="No signal history is available for this coin."
                    >
                        <div className="w-full h-80 text-slate-500 dark:text-slate-400">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart
                                    data={signalSeries}
                                    margin={{
                                        top: 8,
                                        right: 16,
                                        bottom: 8,
                                        left: 8,
                                    }}
                                >
                                    <CartesianGrid
                                        strokeDasharray="3 3"
                                        className="stroke-slate-200 dark:stroke-slate-700"
                                    />
                                    <XAxis
                                        dataKey="date"
                                        tick={{
                                            fontSize: 10,
                                            fill: 'currentColor',
                                        }}
                                        tickLine={false}
                                        minTickGap={40}
                                    />
                                    <YAxis
                                        tick={{
                                            fontSize: 10,
                                            fill: 'currentColor',
                                        }}
                                        tickLine={false}
                                        width={48}
                                        tickFormatter={(v) =>
                                            formatZ(v as number)
                                        }
                                    />
                                    <Tooltip
                                        formatter={(value: number | string) =>
                                            formatZ(value as number)
                                        }
                                        contentStyle={{
                                            fontSize: 12,
                                            borderRadius: 8,
                                        }}
                                    />
                                    <ReferenceLine
                                        y={0}
                                        stroke="currentColor"
                                        strokeOpacity={0.3}
                                    />
                                    <Legend wrapperStyle={{ fontSize: 12 }} />
                                    <Line
                                        type="monotone"
                                        dataKey="forge_ultra_signal"
                                        name="Forge Ultra Signal"
                                        stroke="#f97316"
                                        strokeWidth={2}
                                        dot={false}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </ChartState>
                )}

                {/* FACTORS */}
                {tab === 'factors' && (
                    <ChartState
                        isLoading={isLoading}
                        error={error}
                        isEmpty={factorSeries.length === 0}
                        emptyMessage="No factor history is available for this coin."
                    >
                        <div className="w-full h-80 text-slate-500 dark:text-slate-400">
                            <ResponsiveContainer width="100%" height="100%">
                                <ComposedChart
                                    data={factorSeries}
                                    margin={{
                                        top: 8,
                                        right: 8,
                                        bottom: 8,
                                        left: 8,
                                    }}
                                >
                                    <CartesianGrid
                                        strokeDasharray="3 3"
                                        className="stroke-slate-200 dark:stroke-slate-700"
                                    />
                                    <XAxis
                                        dataKey="date"
                                        tick={{
                                            fontSize: 10,
                                            fill: 'currentColor',
                                        }}
                                        tickLine={false}
                                        minTickGap={40}
                                    />
                                    <YAxis
                                        yAxisId="z"
                                        tick={{
                                            fontSize: 10,
                                            fill: 'currentColor',
                                        }}
                                        tickLine={false}
                                        width={48}
                                        tickFormatter={(v) =>
                                            formatZ(v as number)
                                        }
                                    />
                                    <YAxis
                                        yAxisId="vol"
                                        orientation="right"
                                        tick={{
                                            fontSize: 10,
                                            fill: 'currentColor',
                                        }}
                                        tickLine={false}
                                        width={48}
                                        tickFormatter={(v) =>
                                            `${((v as number) * 100).toFixed(0)}%`
                                        }
                                    />
                                    <Tooltip
                                        formatter={(
                                            value: number | string,
                                            name: string,
                                        ) =>
                                            name === 'Volatility (ann.)'
                                                ? `${((value as number) * 100).toFixed(1)}%`
                                                : formatZ(value as number)
                                        }
                                        contentStyle={{
                                            fontSize: 12,
                                            borderRadius: 8,
                                        }}
                                    />
                                    <ReferenceLine
                                        yAxisId="z"
                                        y={0}
                                        stroke="currentColor"
                                        strokeOpacity={0.3}
                                    />
                                    <Legend wrapperStyle={{ fontSize: 12 }} />
                                    <Bar
                                        yAxisId="vol"
                                        dataKey="annualized_volatility"
                                        name="Volatility (ann.)"
                                        barSize={6}
                                        fill="#94a3b8"
                                        opacity={0.35}
                                    />
                                    <Line
                                        yAxisId="z"
                                        type="monotone"
                                        dataKey="momentum_z_score"
                                        name="Momentum (z)"
                                        stroke="#f97316"
                                        strokeWidth={2}
                                        dot={false}
                                    />
                                    <Line
                                        yAxisId="z"
                                        type="monotone"
                                        dataKey="volatility_z_score"
                                        name="Volatility (z)"
                                        stroke="#0ea5e9"
                                        strokeWidth={2}
                                        dot={false}
                                    />
                                    <Line
                                        yAxisId="z"
                                        type="monotone"
                                        dataKey="liquidity_z_score"
                                        name="Liquidity (z)"
                                        stroke="#10b981"
                                        strokeWidth={2}
                                        dot={false}
                                    />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>
                    </ChartState>
                )}
            </div>
        </div>
    );
};

export default CryptoDetailPopup;
